import Container from '../../../core/components/atoms/Container';
import { useParams } from 'react-router-dom';
import useDeck from '../hooks/use-deck';
import MemoryCard from '../components/card';
import { Typography } from '../../../core/components/molecules/Typography';
import SearchInput from '../components/search-input';
import { useEffect, useState } from 'react';
import ModalCreateFlashcard from '../components/modal-create-flashcard';
import Flashcard from '../components/flashcard';
import { Icon } from '../../../core/components/atoms/Icon';
import MemoryButton from '../components/memory-button';
import { toast } from '../../../core/components/atoms/Toast';
import { deckService, flashcardService } from '../queries';
import { useUserStore } from '../../Auth/stores/authStore';
import { Breadcrumbs } from '../../../core/components/organism/Breadcrumbs';
import ModalDeleteFlashcards from '../components/modal-delete-flashcards';
import BrainLoadingWrapper from '../../../core/components/molecules/BrainLoading';

const DeckPage = () => {
  const { user } = useUserStore();
  const { deckUuid } = useParams();
  const { deck, isLoading, fetchDeck } = useDeck({ deckUuid: deckUuid || '' });
  const userUuid = user?.memoryUserUuid;
  const [search, setSearch] = useState('');

  const [openModal, setOpenModal] = useState(false);
  const [selectedFlashcard, setSelectedFlashcard] = useState<string[]>([]);
  const [isDeleteMode, setIsDeleteMode] = useState(false);

  const [title, setTitle] = useState(deck?.title || '');
  const [editFlashcardUuid, setEditFlashcardUuid] = useState<
    string | undefined
  >(undefined);
  const [isOpenModalDeleteFlashcards, setIsOpenModalDeleteFlashcards] =
    useState(false);

  const filteredFlashcards = deck?.flashcardsDecks?.filter(({ front }) =>
    (front || '').toLowerCase().includes((search || '').toLowerCase())
  );

  const handleSelectFlashcard = (flashcardUuid: string) => {
    setSelectedFlashcard((prev) => {
      if (prev.includes(flashcardUuid)) {
        return prev.filter((uuid) => uuid !== flashcardUuid);
      }
      return [...prev, flashcardUuid];
    });
  };

  const handleSelectAllFlashcards = () => {
    setSelectedFlashcard(
      filteredFlashcards?.map((flashcard) => flashcard.uuid) || []
    );
  };

  const handleCancelDeleteMode = () => {
    setIsDeleteMode(false);
    setSelectedFlashcard([]);
    setIsOpenModalDeleteFlashcards(false);
  };
  const handleDeleteSelectedFlashcards = async () => {
    if (selectedFlashcard.length === 0 || !userUuid) return;

    try {
      const response = await flashcardService.deleteMany(
        userUuid,
        selectedFlashcard
      );
      fetchDeck();
      handleCancelDeleteMode();
      setIsOpenModalDeleteFlashcards(false);
      toast({
        title: 'Flashcards excluídos com sucesso',
        message: response.message,
        variant: 'success',
      });
    } catch (error) {
      console.log('error', error);
      toast({
        title: 'Erro ao deletar flashcards',
        message: 'Ocorreu um erro ao deletar os flashcards',
        variant: 'error',
      });
    }
  };

  const handleEditFlashcard = (flashcardUuid: string) => {
    setEditFlashcardUuid(flashcardUuid);
    setOpenModal(true);
  };

  const handleUpdateDeck = async () => {
    if (!userUuid || !deckUuid) return;

    try {
      await deckService.updateDeck({
        userUuid,
        deckUuid,
        payload: { title },
      });
      fetchDeck();
      setIsDeleteMode(false);

      toast({
        title: 'Deck atualizado com sucesso',
        message: 'O deck foi atualizado com sucesso',
        variant: 'success',
      });
    } catch (error) {
      console.log('error', error);
      toast({
        title: 'Erro ao atualizar deck',
        message: 'Ocorreu um erro ao atualizar o deck',
        variant: 'error',
      });
    }
  };
  useEffect(() => {
    setTitle(deck?.title || '');
  }, [deck]);

  if (isLoading) {
    return (
      <MemoryCard>
        <div className='flex h-[174px]  items-center justify-center'>
          <BrainLoadingWrapper />
        </div>
      </MemoryCard>
    );
  }

  return (
    <Container className='flex flex-col items-center justify-center'>
      <div className='flex w-full flex-col gap-8'>
        <Breadcrumbs
          items={[
            { label: 'Flashcards', href: '/memory' },
            { label: deck?.title || '', href: '#' },
          ]}
        />
        <div className='flex flex-row items-center gap-2'>
          {!isDeleteMode && (
            <Typography className='text-h5 font-bold text-mono-color-darker'>
              {deck?.title}
            </Typography>
          )}
          {isDeleteMode && (
            <input
              type='text'
              value={title}
              className='border flex  items-center overflow-hidden rounded-md border-mono-color-light'
              onChange={(e) => setTitle(e.target.value)}
            />
          )}
        </div>
        <div className='flex w-full flex-col justify-between gap-2 md:flex-row'>
          <div className='w-full md:w-[171px]'>
            <SearchInput
              search={search}
              setSearch={setSearch}
              placeholder='Procure seus Flashcards...'
            />
          </div>
          <div className='flex flex-row items-center gap-4'>
            <ModalCreateFlashcard
              onCallbackSuccess={() => {
                fetchDeck();
              }}
              btnCreateLabel={
                <Typography className='text-color-brand-primary'>
                  Criar <i>Flashcard</i>
                </Typography>
              }
              editFlashcardUuid={editFlashcardUuid}
              open={openModal}
              onClose={() => {
                setOpenModal(false);
                setEditFlashcardUuid(undefined);
              }}
              defaultDeckUuid={deckUuid}
              defaultDiscipline={deck?.slug}
              isDiscipline={deck?.isDiscipline}
            />
            <Icon
              type='material'
              Icon='edit_note'
              className='cursor-pointer  text-color-brand-primary'
              onClick={() => setIsDeleteMode(!isDeleteMode)}
            />
          </div>
        </div>
        {filteredFlashcards?.length === 0 ? (
          <div className='flex h-[412px] flex-col items-center justify-center gap-4 rounded-[32px] bg-mono-color-lighter'>
            <div className='flex h-[174px]  items-center justify-center p-4 text-center'>
              <Typography className='text-xs font-bold !text-mono-color-dark dark:!text-[#2E3847]'>
                Você ainda não tem <i>flashcards</i> aqui. Crie um novo ou
                importe de uma disciplina para começar.
              </Typography>
            </div>
          </div>
        ) : (
          <div className='mb:p-8 grid grid-cols-[repeat(auto-fill,minmax(125px,125px))] justify-center gap-8 rounded-[32px] bg-mono-color-lighter p-2'>
            {filteredFlashcards?.map((flashcard) => (
              <Flashcard
                key={flashcard.uuid}
                title={flashcard.front}
                color={deck?.color}
                icon={deck?.icon}
                uuid={flashcard.uuid}
                onSelect={() => {
                  handleSelectFlashcard(flashcard.uuid);
                }}
                isSelected={selectedFlashcard.includes(flashcard.uuid)}
                isDeleteMode={isDeleteMode}
                onEdit={() => {
                  handleEditFlashcard(flashcard.uuid);
                }}
                onCallbackSuccess={() => {
                  setEditFlashcardUuid(undefined);
                  fetchDeck();
                }}
                discipline={flashcard.discipline?.slug || 'default'}
                subject={flashcard.tags?.[0]?.name}
              />
            ))}
          </div>
        )}
        {isDeleteMode && (
          <div className='flex w-full flex-col justify-between gap-2 md:flex-row'>
            <div className='flex w-full flex-col gap-2 md:flex-row '>
              <MemoryButton
                label='Selecionar todos'
                variant='outline'
                onClick={handleSelectAllFlashcards}
                className='border-none'
                icon={<Icon type='material' Icon='check' className='text-md' />}
                allowReadOnly
              />
              <MemoryButton
                label='Cancelar'
                variant='outline'
                className='w-full md:w-[150px]'
                onClick={handleCancelDeleteMode}
                allowReadOnly
              />
            </div>
            <div className='flex w-full flex-row gap-2 md:w-auto'>
              <div className='flex w-full flex-col items-center gap-2 md:w-auto md:flex-row'>
                <MemoryButton
                  label='Apagar'
                  variant='primary'
                  className='w-full'
                  onClick={() => setIsOpenModalDeleteFlashcards(true)}
                  disabled={selectedFlashcard.length === 0}
                  icon={
                    <Icon type='material' Icon='delete' className='text-md' />
                  }
                />
                <MemoryButton
                  label='Salvar'
                  variant='primary'
                  className='w-full md:w-[150px]'
                  disabled={title === deck?.title}
                  onClick={handleUpdateDeck}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <ModalDeleteFlashcards
        isOpen={isOpenModalDeleteFlashcards}
        onClose={handleCancelDeleteMode}
        onDelete={handleDeleteSelectedFlashcards}
      />
    </Container>
  );
};

export default DeckPage;
